import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  AlertCircle,
  CheckCircle,
  Play,
  Pause,
  Clock,
  BarChart3,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";
import { motion } from 'framer-motion';
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { usePolling } from "../context/PollingContext";
import { useRegistrationRules } from "../contexts/RegistrationRulesContext";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { validateTabulatorPassword, storeTabulatorAuth, checkTabulatorAuth } from "../config/passwordConfig";

import { useAutoApplyScoringRules } from "../hooks/useScoringRulesIntegration";
import { AWARDS_API, fetchWithAuth } from "../utils/api";

import UnifiedStationHeader from "../components/UnifiedStationHeader";
import PerformanceSchedule from "../components/PerformanceSchedule";
import LiveScoreMonitor from "../components/LiveScoreMonitor";
import RoutineMetadataCard from "../components/RoutineMetadataCard";
import { CompactFlexibleScoreDisplay } from "../components/FlexibleScoreDisplay";
import AwardsTracker from "../components/AwardsTracker";

import MagewellRecordingCard from "../components/MagewellRecordingCard";

// Import shared UI components
import StageBackground from "../components/ui/StageBackground";
import DanceParticles from "../components/ui/DanceParticles";
import StageCard from "../components/ui/StageCard";

// Import types
import {
  RoutineData,
  JudgeProfile,
  FinalizedScore,
  CompetitionData,
  TabulatorPageProps,
  ScoreSummaryProps,
  ToastMessage,
  ScoringMode
} from "../types/judge";

// Add debug logging utility
const DEBUG = false;
const debug = (message: string, data: any = null) => {
  if (!DEBUG) return;
  const timestamp = new Date().toISOString();
  if (data) {
    console.log(`[DEBUG ${timestamp}] ${message}`, data);
  } else {
    console.log(`[DEBUG ${timestamp}] ${message}`);
  }
};

// Helper function to parse time string (e.g., "3:30" to seconds)
const parseTimeToSeconds = (timeString: string | undefined): number => {
  if (!timeString) return 0;

  debug("Parsing time string:", timeString);

  const parts = timeString.toString().split(":");
  if (parts.length === 2) {
    const minutes = parseInt(parts[0], 10);
    const seconds = parseInt(parts[1], 10);
    const totalSeconds = minutes * 60 + seconds;
    debug(`Converted ${timeString} to ${totalSeconds} seconds`);
    return totalSeconds;
  }
  return 0;
};

// Helper function to format seconds to MM:SS
const formatSecondsToTime = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

// Helper to get song length from routine object
const getSongLength = (routine: RoutineData | null): string | undefined => {
  if (!routine) return undefined;

  // Try different possible field names
  return (
    routine["Song Length"] ||
    routine.songLength ||
    routine["song length"] ||
    routine["SONG LENGTH"]
  );
};

// Helper to get routine name from routine object
const getRoutineName = (routine: RoutineData | null): string => {
  if (!routine) return "Unknown";

  return (
    routine["Routine Name"] || routine.routineName || routine.name || "Unknown"
  );
};

// Score Summary Component
const ScoreSummary: React.FC<ScoreSummaryProps & { eventTimezone?: string | null }> = ({
  finalizedScores = [],
  routines = [],
  onSelectRoutine,
  className,
  eventTimezone = null,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  // Sort scores by average score (descending)
  const sortedScores = [...finalizedScores].sort(
    (a, b) => b.averageScore - a.averageScore
  );

  // Find routine details by ID
  const getRoutineById = (routineId: string): RoutineData | null => {
    return routines.find((r) => r.routineNumber === routineId) || null;
  };

  // Format submission time using eventTimezone
  const formatSubmissionTime = (timestamp: string): string => {
    if (!timestamp) return "Unknown";
    const date = new Date(timestamp);
    const options: Intl.DateTimeFormatOptions = {};
    if (eventTimezone) {
      options.timeZone = eventTimezone;
    }
    return date.toLocaleTimeString('en-US', options);
  };

  return (
    <div className={`${className || ""}`}>
      <div className="flex justify-between items-center mb-6">
        <h3 
          className="text-xl font-black text-white"
        >
          FINALIZED SCORES
        </h3>
        <span 
          className="text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-400/40 text-green-300 backdrop-blur-xl"
        >
          <span className="font-bold">{sortedScores.length}</span> Scored
        </span>
      </div>

      {sortedScores.length === 0 ? (
        <div 
          className="text-center p-12"
        >
          <div className="mb-4">
            <div
              className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center border border-purple-400/40"
            >
              <BarChart3 className="w-8 h-8 text-purple-400" />
            </div>
          </div>
          <div className="text-white/70 text-lg font-medium">
            No routines have been fully scored yet
          </div>
          <div className="text-white/50 text-sm mt-2">
            Scores will appear here as judges complete their evaluations
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-white/20">
                <th className="px-4 py-3 text-left text-white/80 font-bold">Rank</th>
                <th className="px-4 py-3 text-left text-white/80 font-bold">Routine</th>
                <th className="px-4 py-3 text-center text-white/80 font-bold">Score</th>
                <th className="px-4 py-3 text-center text-white/80 font-bold">Judges</th>
                <th className="px-4 py-3 text-center text-white/80 font-bold">Time</th>
                <th className="px-4 py-3 text-center text-white/80 font-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedScores.map((score, index) => {
                const routine = getRoutineById(score.routineId);

                return (
                  <tr
                    key={score.routineId}
                    className="border-b border-white/10 transition-colors"
                  >
                    <td className="px-4 py-4">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                          index === 0 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                          index === 1 ? 'bg-gradient-to-r from-gray-400 to-gray-500' :
                          index === 2 ? 'bg-gradient-to-r from-orange-600 to-red-600' :
                          'bg-gradient-to-r from-purple-500/40 to-pink-500/40 border border-purple-400/40'
                        }`}
                      >
                        {index + 1}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="font-bold text-white">
                        {routine
                          ? getRoutineName(routine)
                          : `Routine #${score.routineId}`}
                      </div>
                      {routine && (
                        <div className="text-sm text-white/60">
                          {routine.performerName} - {routine.studioCode}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <CompactFlexibleScoreDisplay scoreData={score} />
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="text-white/80 font-semibold">
                        {score.judgeCount}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center text-sm text-white/60">
                      {formatSubmissionTime(score.submittedTime)}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <button
                        onClick={() => onSelectRoutine(score.routineIndex)}
                        className="px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/30 to-indigo-500/30 border border-blue-400/50 text-blue-200 hover:from-blue-500/40 hover:to-indigo-500/40 transition-all duration-300 backdrop-blur-xl"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

interface LocationState {
  competitionName?: string;
  competitionCode?: string;
  eventName?: string;
  stageName?: string;
  scheduleData?: RoutineData[];
}

const TabulatorPage: React.FC<TabulatorPageProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const { competitionCode: urlCompetitionCode } = useParams<{ competitionCode: string }>();
  
  // Set page title
  useEffect(() => {
    document.title = 'Dance Digital | Tabulator';
    return () => {
      document.title = 'Dance Digital'; // Reset on unmount
    };
  }, []);

  // Check tabulator password authorization on component mount
  useEffect(() => {
    const isAuthorized = checkTabulatorAuth();
    
    if (!isAuthorized) {
      // Show password modal instead of redirecting
      setShowPasswordModal(true);
      setIsAuthorized(false);
    } else {
      setIsAuthorized(true);
    }
  }, []);

  // Handle password verification
  const handlePasswordSubmit = () => {
    if (validateTabulatorPassword(password)) {
      // Store tabulator authorization in session
      storeTabulatorAuth();
      
      setShowPasswordModal(false);
      setPassword('');
      setPasswordError('');
      setIsAuthorized(true);
    } else {
      setPasswordError('Incorrect password. Please contact your administrator.');
      setPassword('');
    }
  };
  
  // Debug user info
  useEffect(() => {
    console.log('TabulatorPage - User Info:', user);
    console.log('TabulatorPage - Competition Code from URL:', urlCompetitionCode);
  }, [user, urlCompetitionCode]);
  
  const isDarkMode = theme === "dark";
  
  // Desktop-only layout configuration
  const layout = {
    containerPadding: 'px-8 py-6',
    headerPadding: 'px-8 py-6',
    cardPadding: 'p-6',
    gap: 'gap-6',
    gridCols: 'grid-cols-2 xl:grid-cols-3',
    fontSize: 'text-base',
    headerSize: 'text-xl',
    buttonSize: 'p-3',
    iconSize: 20,
    scale: 1
  };

  const [toast, setToast] = useState<ToastMessage | null>(null);

  // Password protection state
  const [showPasswordModal, setShowPasswordModal] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  
  // Registration rules context for style score sheets
  const { state: registrationRulesState } = useRegistrationRules();
  const styleScoreSheets = registrationRulesState.styleScoreSheets || [];
  
  // Scoring mode detection for LiveScoreMonitor
  const [scoringMode, setScoringMode] = useState<ScoringMode>('simple');
  const [scoringModeLoading, setScoringModeLoading] = useState<boolean>(true);
  
  const { 
    connected, 
    loading,
    competitionCode, 
    currentRoutine,
    currentIndex,
    scheduleData: routines,
    connectedJudges,
    judgeProfiles,
    recordingStatus,
    uploadSchedule,
    changeCurrentRoutine, 
    startRoutineTimer, 
    stopRoutineTimer, 
    getAllFinalizedScores,
    startSession,
    updateScore,
    submitScore,
    deleteScore,
    scoreUpdates,
    eventTimezone,
    // Auto-reconnection status
    isReconnecting,
    reconnectAttempts
  } = usePolling();
  
  const locationState = useLocation() as { state?: LocationState };
  const competitionData: CompetitionData = locationState.state || {};
  
  // Auto-apply scoring rules when joining as tabulator
  useAutoApplyScoringRules(competitionCode, 'tabulator');
  
  // Fetch scoring mode for LiveScoreMonitor
  useEffect(() => {
    const fetchScoringMode = async () => {
      try {
        setScoringModeLoading(true);
        console.log('🔍 TabulatorPage: Fetching scoring mode from AWARDS_API...');
        
        // Get scoring mode from AWARDS_API (where scoring configuration is stored)
        const data = await fetchWithAuth(AWARDS_API);
        const apiScoringMode = data.scoringMode || 'simple';
        
        console.log('🔍 TabulatorPage: Scoring mode from API:', apiScoringMode);
        setScoringMode(apiScoringMode);
        setScoringModeLoading(false);
        
      } catch (err) {
        console.error("🔍 TabulatorPage: Error loading scoring mode:", err);
        
        // Robust fallback logic when APIs are failing
        const hasStyleSheets = styleScoreSheets && styleScoreSheets.length > 0;
        
        // For now, default to simple mode when APIs fail (can be overridden later)
        // This ensures LiveScoreMonitor shows the correct layout even when backend is having issues
        console.log("🔍 TabulatorPage: API failed, defaulting to simple scoring mode for LiveScoreMonitor.");
        setScoringMode('simple');
        
        setScoringModeLoading(false);
      }
    };

    // Only fetch if we have a competition code
    if (competitionCode) {
      fetchScoringMode();
    }
  }, [competitionCode, styleScoreSheets]);
  const [competitionName, setCompetitionName] = useState<string>(competitionData.competitionName || "Competition");
  const [socketStatus, setSocketStatus] = useState<string>("Disconnected");

  // Convert Routine to RoutineData type for compatibility
  const convertRoutineToRoutineData = (routine: any): RoutineData | null => {
    if (!routine) return null;
    
    const converted = {
      ...routine,
      routineNumber: routine.number || routine.routineNumber || routine.id || "unknown"
    } as RoutineData;

    return converted;
  };

  // Convert current routine and routines for compatibility
  const currentRoutineData = convertRoutineToRoutineData(currentRoutine);
  const routinesData = routines.map(convertRoutineToRoutineData).filter(Boolean) as RoutineData[];
  const [finalizedScores, setFinalizedScores] = useState<FinalizedScore[]>([]);
  const [showScoreSummary, setShowScoreSummary] = useState<boolean>(false);
  const [isBlurred, setIsBlurred] = useState<boolean>(false);


  // Timer state
  const [isTimerRunning, setIsTimerRunning] = useState<boolean>(false);
  const [timerSeconds, setTimerSeconds] = useState<number>(0);
  const [totalSeconds, setTotalSeconds] = useState<number>(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [endTime, setEndTime] = useState<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Save theme preference
  useEffect(() => {
    localStorage.setItem("tabulator_dark_mode", isDarkMode.toString());
  }, [isDarkMode]);

  // Polling connection status
  useEffect(() => {
    if (connected && competitionCode) {
      setSocketStatus("Connected");
      debug("Polling connected");

      // In the simplified approach, session is already started from CompetitionCreation
      // No need to join again - just log the connection
      const effectiveCompetitionCode = competitionData.competitionCode || competitionCode;
      if (effectiveCompetitionCode && !competitionCode) {
        debug(`Connected to session ${effectiveCompetitionCode} as tabulator`);
      }

      // In the polling architecture, all data (including scheduleData) is loaded from AWS
      // via loadCompetitionFromAWS. The tabulator doesn't need to do anything special.
      debug("Tabulator connected - polling context will load all data from AWS");

      // Request all finalized scores
        getAllFinalizedScores();
    }
  }, [connected, competitionCode, getAllFinalizedScores]);

  // Handle disconnected state separately
  useEffect(() => {
    if (!connected) {
      setSocketStatus("Disconnected");
      debug("Polling disconnected");
    }
  }, [connected]);


  // Judge connections are handled automatically by polling
  // No need for WebSocket listeners

  // Judge profile updates are handled automatically by polling
  // No need for WebSocket listeners

  // Schedule updates are handled automatically by polling
  // No need for WebSocket listeners

  // Current routine changes are handled automatically by polling
  // No need for WebSocket listeners

  // Finalized scores are handled automatically by polling
  // No need for WebSocket listeners

  // Timer events are handled automatically by polling
  // No need for WebSocket listeners

  // Add this handler for recording state changes
  const handleRecordingStateChange = useCallback((state: 'recording' | 'paused' | 'stopped') => {
    if (state === 'recording') {
      // Start the timer when recording starts
      if (!isTimerRunning && currentRoutine) {
        const songLength = getSongLength(currentRoutineData);
        const durationSeconds = songLength ? parseTimeToSeconds(songLength) : 180;
        
        const now = Date.now();
        setStartTime(now);
        setEndTime(now + durationSeconds * 1000);
        setTimerSeconds(durationSeconds);
        setTotalSeconds(durationSeconds);
        setIsTimerRunning(true);
        
        if (connected) {
          startRoutineTimer({
            routineId: currentRoutine.id,
            routineIndex: currentIndex,
            startTime: new Date(now).toISOString(),
            duration: durationSeconds,
          });
        }
      }
    } else if (state === 'paused') {
      // Pause the timer when recording is paused
      setIsTimerRunning(false);
      if (connected) {
        stopRoutineTimer({
          routineId: currentRoutine?.id || '',
          routineIndex: currentIndex,
          completed: false
        });
      }
    } else if (state === 'stopped') {
      // Stop the timer when recording is stopped
      setIsTimerRunning(false);
      setStartTime(null);
      setEndTime(null);
      if (connected) {
        stopRoutineTimer({
          routineId: currentRoutine?.id || '',
          routineIndex: currentIndex,
          completed: false
        });
      }
    }
  }, [currentRoutine, currentIndex, connected, isTimerRunning, startRoutineTimer, stopRoutineTimer]);

  // Update Timer logic to not stop recording when timer completes
  useEffect(() => {
    if (isTimerRunning && endTime) {
      timerRef.current = setInterval(() => {
        const currentTime = Date.now();
        const remainingMs = Math.max(0, endTime - currentTime);
        const remainingSeconds = Math.ceil(remainingMs / 1000);

        setTimerSeconds(remainingSeconds);

        if (remainingMs <= 0) {
          // Timer complete - but don't stop recording
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          setIsTimerRunning(false);

          // Notify that timer is complete using polling context
          if (connected) {
            stopRoutineTimer({
              routineId: currentRoutine?.id || '',
              routineIndex: currentIndex,
              completed: true,
              recordingContinues: true
            });
          }
          
          // Show toast notification that time is up
          setToast({
            type: "warning",
            message: `Time is up for routine: ${getRoutineName(currentRoutineData)}. Recording continues.`,
          });
        }
      }, 100); // Update more frequently for smoother countdown
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isTimerRunning, endTime, connected, currentRoutine, currentIndex, stopRoutineTimer]);

  // Auto-dismiss toast after 5 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [toast]);

  // When routine changes, reset timer
  useEffect(() => {
    setIsTimerRunning(false);
    const songLength = getSongLength(currentRoutineData);

    if (songLength) {
      debug("Found song length for routine:", songLength);
      const seconds = parseTimeToSeconds(songLength);
      setTimerSeconds(seconds);
      setTotalSeconds(seconds);
    } else {
      debug("No song length found for routine:", currentRoutine);
      setTimerSeconds(0);
      setTotalSeconds(0);
    }
  }, [currentRoutine]);

  // Handle routine navigation
  const handlePreviousRoutine = useCallback(() => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      const newRoutine = routines[newIndex];
      
      // Use polling context to change routine
      changeCurrentRoutine({
        index: newIndex,
        routine: newRoutine
      });

      // Stop any running timer
      if (isTimerRunning) {
        setIsTimerRunning(false);
        if (connected) {
          stopRoutineTimer({
            routineId: currentRoutine?.id || '',
            routineIndex: currentIndex,
          });
        }
      }

        setToast({
          type: "info",
          message: `Changed to routine: ${getRoutineName(
            convertRoutineToRoutineData(routines[newIndex])
          )}`,
        });
    }
  }, [
    currentIndex,
    routines,
    connected,
    isTimerRunning,
    currentRoutine,
    stopRoutineTimer,
    changeCurrentRoutine,
  ]);

  const handleNextRoutine = useCallback(() => {
    if (currentIndex < routines.length - 1) {
      const newIndex = currentIndex + 1;
      const newRoutine = routines[newIndex];
      
      // Use polling context to change routine
      changeCurrentRoutine({
        index: newIndex,
        routine: newRoutine
      });

      // Stop any running timer
      if (isTimerRunning) {
        setIsTimerRunning(false);
        if (connected) {
          stopRoutineTimer({
            routineId: currentRoutine?.id || '',
            routineIndex: currentIndex,
          });
        }
      }

        setToast({
          type: "info",
          message: `Changed to routine: ${getRoutineName(
            convertRoutineToRoutineData(routines[newIndex])
          )}`,
        });
    }
  }, [
    currentIndex,
    routines,
    connected,
    isTimerRunning,
    currentRoutine,
    stopRoutineTimer,
    changeCurrentRoutine,
  ]);

  const handleJumpToRoutine = useCallback((routineNumber: number) => {
    // Find the routine by routine number (1-based)
    const routineIndex = routines.findIndex(routine => {
      const num = routine.number || routine.routineNumber || routine['Routine #'] || routine.id;
      return parseInt(num?.toString() || '0') === routineNumber;
    });
    
    if (routineIndex >= 0 && routineIndex < routines.length) {
      const newRoutine = routines[routineIndex];
      
      // Use polling context to change routine
      changeCurrentRoutine({
        index: routineIndex,
        routine: newRoutine
      });

      // Stop any running timer
      if (isTimerRunning) {
        setIsTimerRunning(false);
        if (connected) {
          stopRoutineTimer({
            routineId: currentRoutine?.id || '',
            routineIndex: currentIndex,
          });
        }
      }

        setToast({
          type: "info",
          message: `Jumped to routine: ${getRoutineName(
            convertRoutineToRoutineData(routines[routineIndex])
          )}`,
        });
    }
  }, [
    routines,
    connected,
    isTimerRunning,
    currentRoutine,
    currentIndex,
    stopRoutineTimer,
    changeCurrentRoutine,
  ]);

  const handleScratchCurrentRoutine = useCallback(() => {
    if (currentRoutine && routines[currentIndex]) {
      // Mark the current routine as scratched
      const updatedRoutines = [...routines];
      updatedRoutines[currentIndex] = {
        ...updatedRoutines[currentIndex],
        scratched: true
      };
      
      // Update the schedule
      uploadSchedule(updatedRoutines as any);
      
      setToast({
        type: "warning",
        message: `Scratched routine: ${getRoutineName(
          convertRoutineToRoutineData(routines[currentIndex])
        )}`,
      });
    }
  }, [currentRoutine, routines, currentIndex, uploadSchedule]);

  const handleUnscratchCurrentRoutine = useCallback(() => {
    if (currentRoutine && routines[currentIndex]) {
      // Remove the scratched status from the current routine
      const updatedRoutines = [...routines];
      updatedRoutines[currentIndex] = {
        ...updatedRoutines[currentIndex],
        scratched: false
      };
      
      // Update the schedule
      uploadSchedule(updatedRoutines as any);
      
      setToast({
        type: "success",
        message: `Unscratched routine: ${getRoutineName(
          convertRoutineToRoutineData(routines[currentIndex])
        )}`,
      });
    }
  }, [currentRoutine, routines, currentIndex, uploadSchedule]);

  // Handle routine selection from PerformanceSchedule
  const handleRoutineSelect = useCallback(
    (index: number) => {
      if (Array.isArray(routines) && index >= 0 && index < routines.length) {
        const selectedRoutine = routines[index];
        
        // Update local state only - no backend sync needed
        changeCurrentRoutine({
          index: index,
          routine: selectedRoutine
        });

        // Stop any running timer
        if (isTimerRunning) {
          setIsTimerRunning(false);
          if (connected) {
            stopRoutineTimer({
              routineId: currentRoutine?.id || '',
              routineIndex: currentIndex,
            });
          }
        }

          setToast({
            type: "info",
            message: `Changed to routine: ${getRoutineName(
            convertRoutineToRoutineData(selectedRoutine)
            )}`,
          });
      }
    },
    [routines, connected, isTimerRunning, currentRoutine, currentIndex, stopRoutineTimer, changeCurrentRoutine]
  );

  // Handle CSV upload
  const handleRoutinesUpload = useCallback(
    (newRoutines: RoutineData[]) => {
      if (Array.isArray(newRoutines) && newRoutines.length > 0) {
        // Log the first routine to check its structure
        debug("First routine from CSV:", newRoutines[0]);

        // Log all keys to see what field names we actually have
        debug("Available fields:", Object.keys(newRoutines[0]));

        // Check for Song Length specifically
        const songLength = getSongLength(newRoutines[0]);
        debug("Song Length field value:", songLength);

        // Ensure routineNumber is present for compatibility
        const processedRoutines = newRoutines.map((routine: any, index: number) => ({
          ...routine,
          routineNumber: routine.number || routine.routineNumber || routine.id || routine['Routine #'] || `routine-${index}`
        }));

        // Reset scores when loading new routines
        setFinalizedScores([]);

        // Trigger auto-save when new routines are loaded
        setTimeout(() => {
('normal');
        }, 500);

        // Reset timer state
        setIsTimerRunning(false);
        if (songLength) {
          const seconds = parseTimeToSeconds(songLength);
          setTimerSeconds(seconds);
          setTotalSeconds(seconds);
        }

        // Broadcast the schedule to all connected judges
        if (connected) {
          debug("Broadcasting schedule to judges", processedRoutines);
          uploadSchedule(processedRoutines);

          // Set the current routine to the first one
          changeCurrentRoutine({
            index: 0,
            routine: processedRoutines[0],
          });

          setToast({
            type: "success",
            message: `Loaded ${processedRoutines.length} routines and broadcasted to judges`,
          });
        } else {
          setToast({
            type: "warning",
            message: `Loaded ${processedRoutines.length} routines, but unable to broadcast (disconnected)`,
          });
        }
      }
    },
    [connected, uploadSchedule, changeCurrentRoutine, ]
  );

  // Calculate progress percentage for timer
  const timerProgress =
    totalSeconds > 0 ? ((totalSeconds - timerSeconds) / totalSeconds) * 100 : 0;

  // Handle blur toggle for privacy lock screen
  const toggleBlur = (override?: boolean) => {
    const newBlurState = override !== undefined ? override : !isBlurred;
    setIsBlurred(newBlurState);
    setToast({
      type: 'info',
      message: `Screen ${newBlurState ? 'locked' : 'unlocked'}`
    });
  };

  // Handle routine scratch
  const handleRoutineScratch = useCallback((routineIndex: number, routine: RoutineData) => {
    if (routineIndex >= 0 && routineIndex < routines.length) {
      const updatedRoutines = [...routines];
      updatedRoutines[routineIndex] = {
        ...updatedRoutines[routineIndex],
        scratched: true
      };
      
      // Broadcast the updated schedule to judges using polling context
      if (connected) {
        uploadSchedule(updatedRoutines);
        setToast({
          type: "warning",
          message: `Routine ${routine.routineNumber || routineIndex + 1} has been scratched`,
        });
      }
      
      // Trigger auto-save
      setTimeout(() => {
      }, 100);
    }
  }, [routines, connected, uploadSchedule, , currentIndex]);

  // Handle routine unscratch
  const handleRoutineUnscratch = useCallback((routineIndex: number, routine: RoutineData) => {
    if (routineIndex >= 0 && routineIndex < routines.length) {
      const updatedRoutines = [...routines];
      updatedRoutines[routineIndex] = {
        ...updatedRoutines[routineIndex],
        scratched: false
      };
      
      // Broadcast the updated schedule to judges using polling context
      if (connected) {
        uploadSchedule(updatedRoutines);
        setToast({
          type: "success",
          message: `Routine ${routine.routineNumber || routineIndex + 1} has been unscratched`,
        });
      }
      
      // Trigger auto-save
      setTimeout(() => {
      }, 100);
    }
  }, [routines, connected, uploadSchedule, , currentIndex]);




  // Handle inline score editing
  const handleScoreUpdate = useCallback(async (judgeId: string, criterionId: string, newScore: number) => {
    if (!currentRoutine || !updateScore) {
      setToast({
        type: "error",
        message: "Unable to update score - no active routine or connection",
      });
      return;
    }

    try {
      // Use the SAME routine identifier that judges and LiveScoreMonitor use for scoring
      // Priority: number > id > name > fallback to string conversion of currentIndex
      const routineId = currentRoutine.number || 
                       currentRoutine.id || 
                       currentRoutine.name ||
                       currentIndex.toString();

      console.log('Routine ID determination:', {
        routineNumber: currentRoutine.routineNumber,
        number: currentRoutine.number,
        id: currentRoutine.id,
        name: currentRoutine.name,
        currentIndex,
        finalRoutineId: routineId
      });

      // Get existing score data to preserve submission status and timestamp
      const scoreKey = `${routineId}_${judgeId}`;
      const existingScoreData = scoreUpdates[scoreKey];
      
      console.log('Existing score data:', existingScoreData);

      // Preserve existing scores and merge in the new score
      const existingScores = existingScoreData?.scores || {};
      const updatedScores = { ...existingScores, [criterionId]: newScore };

      // Convert to the expected ScoreData format
      const scoreData = {
        judgeId,
        routineId, // Use the consistent routine ID
        routineIndex: currentIndex,
        scores: updatedScores, // Merge new score with existing scores
        isSubmitted: true, // Tabulator edits should be treated as submissions
        comments: existingScoreData?.comments || '', // Preserve existing comments
        styleFeedback: existingScoreData?.styleFeedback, // Preserve existing style feedback
        // Preserve boolean flags - CRITICAL for maintaining choreographer transparency
        iChoreographedThis: existingScoreData?.iChoreographedThis || false,
        notFamilyFriendly: existingScoreData?.notFamilyFriendly || false,
        // Set submission timestamp if not already set
        submittedTime: existingScoreData?.submittedTime || new Date().toISOString(),
      };

      console.log('Updating score via AWS (preserving submission status):', scoreData);
      
      // Call the polling context updateScore function which hits AWS
      const success = await updateScore(scoreData);
      
      if (success) {
        setToast({
          type: "success",
          message: `Score updated for ${judgeId}`,
        });
      } else {
        throw new Error('Update score returned false');
      }
    } catch (error) {
      console.error('Failed to update score:', error);
      setToast({
        type: "error",
        message: `Failed to update score for ${judgeId}`,
      });
    }
  }, [currentRoutine, currentIndex, updateScore, scoreUpdates]);

  // Handle inline comment editing
  const handleCommentUpdate = useCallback(async (judgeId: string, newComment: string) => {
    if (!currentRoutine || !updateScore) {
      setToast({
        type: "error",
        message: "Unable to update comment - no active routine or connection",
      });
      return;
    }

    try {
      // Use the SAME routine identifier that judges and LiveScoreMonitor use for scoring
      const routineId = currentRoutine.number || 
                       currentRoutine.id || 
                       currentRoutine.name ||
                       currentIndex.toString();

      // Get existing score data to preserve submission status and scores
      const scoreKey = `${routineId}_${judgeId}`;
      const existingScoreData = scoreUpdates[scoreKey];

      // Trim and validate comment
      const trimmedComment = newComment.trim();
      const maxCommentLength = 1000;
      
      if (trimmedComment.length > maxCommentLength) {
        setToast({
          type: "warning",
          message: "Comment was truncated to 1000 characters",
        });
      }

      const finalComment = trimmedComment.length > maxCommentLength 
        ? trimmedComment.substring(0, maxCommentLength)
        : trimmedComment;

      // For comment-only updates, preserve all existing data
      const scoreData = {
        judgeId,
        routineId, // Use the consistent routine ID
        routineIndex: currentIndex,
        scores: existingScoreData?.scores || {}, // Preserve existing scores
        isSubmitted: existingScoreData?.isSubmitted || false, // Preserve submission status
        comments: finalComment,
        styleFeedback: existingScoreData?.styleFeedback, // Preserve existing style feedback
        // Preserve boolean flags - CRITICAL for maintaining choreographer transparency
        iChoreographedThis: existingScoreData?.iChoreographedThis || false,
        notFamilyFriendly: existingScoreData?.notFamilyFriendly || false,
        // Preserve submission timestamp if it exists
        ...(existingScoreData?.submittedTime && { submittedTime: existingScoreData.submittedTime }),
      };

      console.log('Updating comment via AWS (preserving submission status):', scoreData);
      
      // Call the polling context updateScore function which hits AWS
      const success = await updateScore(scoreData);
      
      if (success) {
        setToast({
          type: "success",
          message: `Comment updated for ${judgeId}`,
        });
      } else {
        throw new Error('Update comment returned false');
      }
    } catch (error) {
      console.error('Failed to update comment:', error);
      setToast({
        type: "error",
        message: `Failed to update comment for ${judgeId}`,
      });
    }
  }, [currentRoutine, currentIndex, updateScore, scoreUpdates]);

  // Handle style feedback editing
  const handleStyleFeedbackUpdate = useCallback(async (judgeId: string, category: string, criteria: string, feedbackType: 'good' | 'needs-work' | null, batchedStyleFeedback?: any) => {
    console.log('handleStyleFeedbackUpdate - Raw arguments:', {
      judgeId,
      category,
      criteria, 
      feedbackType,
      batchedStyleFeedback,
      batchedStyleFeedbackType: typeof batchedStyleFeedback,
      hasBatchedData: batchedStyleFeedback !== undefined
    });
    if (!currentRoutine || !updateScore) {
      setToast({
        type: "error",
        message: "Unable to update style feedback - no active routine or connection",
      });
      return;
    }

    try {
      // Use the SAME routine identifier that judges and LiveScoreMonitor use for scoring
      const routineId = currentRoutine.number || 
                       currentRoutine.id || 
                       currentRoutine.name ||
                       currentIndex.toString();

      // Get existing score data to preserve submission status and scores
      const scoreKey = `${routineId}_${judgeId}`;
      const existingScoreData = scoreUpdates[scoreKey];

      if (!existingScoreData) {
        setToast({
          type: "error",
          message: "No existing score data found for this judge",
        });
        return;
      }

      console.log('handleStyleFeedbackUpdate called with:', {
        judgeId,
        category,
        criteria,
        feedbackType,
        hasBatchedData: !!batchedStyleFeedback,
        batchedStyleFeedback: batchedStyleFeedback ? JSON.stringify(batchedStyleFeedback, null, 2) : 'none'
      });

      // Create updated style feedback
      let updatedStyleFeedback;
      
      if (batchedStyleFeedback) {
        // Handle batched update (from modal)
        console.log('Processing batched style feedback update');
        updatedStyleFeedback = batchedStyleFeedback;
      } else {
        // Handle individual update (legacy support)
        const existingStyleFeedback = existingScoreData.styleFeedback || { good: [], 'needs-work': [] };
        updatedStyleFeedback = {
          good: [...(existingStyleFeedback.good || [])],
          'needs-work': [...(existingStyleFeedback['needs-work'] || [])]
        };

        // Remove existing feedback for this criteria from both categories
        updatedStyleFeedback.good = updatedStyleFeedback.good.filter(item => 
          !(item.category === category && item.criteria === criteria)
        );
        updatedStyleFeedback['needs-work'] = updatedStyleFeedback['needs-work'].filter(item => 
          !(item.category === category && item.criteria === criteria)
        );

        // Add to new category if feedbackType is specified
        if (feedbackType) {
          updatedStyleFeedback[feedbackType].push({ category, criteria });
        }
      }

      // For style feedback updates, preserve all existing data
      const scoreData = {
        judgeId,
        routineId,
        routineIndex: currentIndex,
        scores: existingScoreData.scores || {},
        isSubmitted: existingScoreData.isSubmitted || false,
        comments: existingScoreData.comments || '',
        styleFeedback: updatedStyleFeedback,
        // Preserve boolean flags - CRITICAL for maintaining choreographer transparency
        iChoreographedThis: existingScoreData.iChoreographedThis || false,
        notFamilyFriendly: existingScoreData.notFamilyFriendly || false,
        // Preserve submission timestamp if it exists
        ...(existingScoreData.submittedTime && { submittedTime: existingScoreData.submittedTime }),
      };

      console.log('Updating style feedback via AWS (preserving submission status):', {
        ...scoreData,
        styleFeedbackDetailed: JSON.stringify(scoreData.styleFeedback, null, 2)
      });
      
      // Call the polling context updateScore function which hits AWS
      const success = await updateScore(scoreData);
      
      if (success) {
        setToast({
          type: "success",
          message: `Style feedback updated for ${judgeId}`,
        });
      } else {
        throw new Error('Update style feedback returned false');
      }
    } catch (error) {
      console.error('Failed to update style feedback:', error);
      setToast({
        type: "error",
        message: `Failed to update style feedback for ${judgeId}`,
      });
    }
  }, [currentRoutine, currentIndex, updateScore, scoreUpdates]);

  // Handle boolean flag updates (iChoreographedThis, notFamilyFriendly)
  const handleBooleanFlagUpdate = useCallback(async (judgeId: string, flagType: 'iChoreographedThis' | 'notFamilyFriendly', newValue: boolean) => {
    console.log('TabulatorPage - handleBooleanFlagUpdate called:', {
      judgeId,
      flagType,
      newValue
    });

    if (!currentRoutine) {
      console.error('No current routine for boolean flag update');
      setToast({
        type: "error",
        message: "No routine selected for flag update",
      });
      return;
    }

    try {
      // Use the SAME routine identifier that judges and LiveScoreMonitor use for scoring
      const routineId = currentRoutine.number || 
                       currentRoutine.id || 
                       currentRoutine.name ||
                       currentIndex.toString();

      // Get existing score data to preserve submission status and scores
      const scoreKey = `${routineId}_${judgeId}`;
      const existingScoreData = scoreUpdates[scoreKey];

      console.log('Existing score data for boolean flag update:', existingScoreData);

      // Prepare the score data with the updated boolean flag
      let updatedScores = existingScoreData?.scores || {};
      
      // If setting iChoreographedThis to true, zero out all scores
      if (flagType === 'iChoreographedThis' && newValue === true) {
        console.log('Choreographer flag set to true - zeroing out all scores');
        console.log('Original scores:', updatedScores);
        
        // Create a new scores object with all values set to 0
        updatedScores = {};
        const originalScores = existingScoreData?.scores || {};
        
        // Zero out all existing score fields
        for (const [scoreKey, scoreValue] of Object.entries(originalScores)) {
          updatedScores[scoreKey] = 0;
        }
        
        // Also ensure simpleScore is 0 if it exists
        if (originalScores.simpleScore !== undefined || existingScoreData?.simpleScore !== undefined) {
          updatedScores.simpleScore = 0;
        }
        
        console.log('Updated scores (all zeroed):', updatedScores);
      }
      
      const scoreData = {
        judgeId,
        routineId,
        routineIndex: currentIndex,
        scores: updatedScores,
        isSubmitted: existingScoreData?.isSubmitted || false,
        comments: existingScoreData?.comments || '',
        styleFeedback: existingScoreData?.styleFeedback,
        // Preserve both boolean flags and update the specific one
        iChoreographedThis: flagType === 'iChoreographedThis' ? newValue : (existingScoreData?.iChoreographedThis || false),
        notFamilyFriendly: flagType === 'notFamilyFriendly' ? newValue : (existingScoreData?.notFamilyFriendly || false),
        // Preserve submission timestamp if it exists
        ...(existingScoreData?.submittedTime && { submittedTime: existingScoreData.submittedTime }),
      };

      console.log('Updating boolean flag via AWS:', scoreData);
      
      const success = await updateScore(scoreData);
      
      if (success) {
        const flagDisplayName = flagType === 'iChoreographedThis' ? 'choreographer status' : 'family-friendly status';
        setToast({
          type: "success",
          message: `${flagDisplayName} updated successfully`,
        });
      } else {
        throw new Error(`Failed to update ${flagType}`);
      }

    } catch (error) {
      console.error('Error updating boolean flag:', error);
      const flagDisplayName = flagType === 'iChoreographedThis' ? 'choreographer status' : 'family-friendly status';
      setToast({
        type: "error",
        message: `Failed to update ${flagDisplayName}`,
      });
    }
  }, [currentRoutine, currentIndex, scoreUpdates, updateScore]);

  // Handle score deletion
  const handleScoreDelete = useCallback(async (judgeId: string, routineId: string) => {
    try {
      console.log(`TabulatorPage - handleScoreDelete called: judgeId=${judgeId}, routineId=${routineId}`);
      
      const success = await deleteScore(judgeId, routineId);
      
      if (success) {
        setToast({
          type: "success",
          message: `Score deleted successfully for ${judgeId}`,
        });
      } else {
        throw new Error('Failed to delete score');
      }

    } catch (error) {
      console.error('Error deleting score:', error);
      setToast({
        type: "error",
        message: `Failed to delete score for ${judgeId}`,
      });
    }
  }, [deleteScore]);

  // Helper function to calculate average scores
  const calculateAverageScores = useCallback((judgeScoresData: any[]) => {
    if (judgeScoresData.length === 0) return {};

    const criteriaMap = new Map();
    
    // Collect all scores by criterion
    judgeScoresData.forEach(scoreData => {
      Object.entries(scoreData.scores || {}).forEach(([criterion, score]) => {
        if (!criteriaMap.has(criterion)) {
          criteriaMap.set(criterion, []);
        }
        criteriaMap.set(criterion, [...criteriaMap.get(criterion), parseFloat(score as string)]);
      });
    });

    // Calculate averages
    const averageScores: { [key: string]: number } = {};
    criteriaMap.forEach((scores, criterion) => {
      const sum = scores.reduce((acc: number, score: number) => acc + score, 0);
      averageScores[criterion] = Math.round((sum / scores.length) * 100) / 100; // Round to 2 decimal places
    });

    return averageScores;
  }, []);

  // Handle choreographer score replacement with average of other judges
  const handleChoreographerScoreReplacement = useCallback(async (judgeId: string) => {
    if (!currentRoutine) {
      setToast({
        type: "error",
        message: "No routine selected for score replacement",
      });
      return;
    }

    try {
      const routineId = currentRoutine.number || 
                       currentRoutine.id || 
                       currentRoutine.name ||
                       currentIndex.toString();

      // Get all scores for this routine
      const routineScores = Object.entries(scoreUpdates)
        .filter(([key]) => key.startsWith(`${routineId}_`))
        .map(([key, scoreData]) => ({
          judgeId: key.split('_').slice(1).join('_'),
          scoreData
        }));

      // Find other judges' scores (excluding the choreographer)
      const otherJudgesScores = routineScores.filter(
        score => score.judgeId !== judgeId && 
                 score.scoreData.isSubmitted && 
                 !score.scoreData.iChoreographedThis
      );

      if (otherJudgesScores.length < 2) {
        setToast({
          type: "error",
          message: "Need at least 2 other submitted scores to calculate average",
        });
        return;
      }

      // Calculate average scores for each criterion
      const averageScores = calculateAverageScores(otherJudgesScores.map(j => j.scoreData));
      
      // Get existing choreographer score data
      const choreographerScoreKey = `${routineId}_${judgeId}`;
      const existingScoreData = scoreUpdates[choreographerScoreKey];

      // Update the choreographer's score with the average, but keep iChoreographedThis: true
      const scoreData = {
        judgeId,
        routineId,
        routineIndex: currentIndex,
        scores: averageScores,
        isSubmitted: existingScoreData?.isSubmitted || true,
        comments: existingScoreData?.comments || 'Score replaced with average of other judges',
        styleFeedback: existingScoreData?.styleFeedback,
        iChoreographedThis: true, // Keep this true for transparency
        notFamilyFriendly: existingScoreData?.notFamilyFriendly || false,
        submittedTime: existingScoreData?.submittedTime || new Date().toISOString(),
        isReplacementScore: true, // Add flag to indicate this is a replacement
        replacementReason: 'Average of other judges due to choreographer conflict'
      };

      console.log('Replacing choreographer score with average:', scoreData);
      
      const success = await updateScore(scoreData);
      
      if (success) {
        setToast({
          type: "success",
          message: "Choreographer score replaced with average of other judges",
        });
      } else {
        throw new Error('Failed to update choreographer score');
      }

    } catch (error) {
      console.error('Error replacing choreographer score:', error);
      setToast({
        type: "error",
        message: "Failed to replace choreographer score",
      });
    }
  }, [currentRoutine, currentIndex, scoreUpdates, updateScore, calculateAverageScores]);

  // Update keyboard shortcut for privacy lock screen
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'l') {
        event.preventDefault();
        toggleBlur();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isBlurred]);

  // Main component render
  // Show loading screen while competition data is being fetched
  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold mb-2">Loading Competition</h2>
          <p className="text-white/60">
            {urlCompetitionCode ? `Loading ${urlCompetitionCode}...` : 'Fetching competition data...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Stage Background and Particles */}
      <StageBackground />
      <DanceParticles />

      {/* Password Modal Overlay */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-8 max-w-md w-full"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Tabulator Access</h3>
              <p className="text-white/70">Enter the password to access tabulator</p>
            </div>
            
            <div className="space-y-4">
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                  placeholder="Enter password"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:border-blue-400/50 focus:bg-white/15 transition-all pr-12"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {passwordError && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-400 text-sm text-center"
                >
                  {passwordError}
                </motion.p>
              )}
              
              <button
                onClick={handlePasswordSubmit}
                className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all font-semibold"
              >
                Access Tabulator
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Main Content - Only show when authorized */}
      {isAuthorized && (
      
      <div
        className="fixed inset-0 flex flex-col overflow-hidden bg-black text-white no-overscroll"
        style={{ 
          filter: isBlurred ? 'blur(20px)' : 'none',
          transition: 'filter 0.3s ease'
        }}
      >
        <div className="w-full flex flex-col flex-grow min-h-0 overflow-hidden relative z-20">
          {/* Current Routine Header - flex-shrink-0 */}
          <div className="flex-shrink-0 px-6 pt-4 pb-4">
            <UnifiedStationHeader
              stationType="tabulator"
              tabulatorTitle="TABULATOR STATION"
              tabulatorSubtitle=""
              tabulatorConnected={connected}
              tabulatorStatus={socketStatus}
              tabulatorRoomId={competitionCode}
              tabulatorIsBlurred={isBlurred}
              onTabulatorToggleBlur={() => toggleBlur()}
              onTabulatorScoreSummary={() => setShowScoreSummary(!showScoreSummary)}
              tabulatorIsReconnecting={isReconnecting}
              tabulatorReconnectAttempts={reconnectAttempts}
              onTabulatorViewAwards={() => {
                const competitionCodeForAwards = competitionCode;
                if (!competitionCodeForAwards) return;
                
                // Extract base competition code (remove stage and section suffixes)
                // Examples: 
                // 5d7ebe4f_hitchkick-competition-1_001_set-1 -> 5d7ebe4f_hitchkick-competition-1
                // 5d7ebe4f_hitchkick-competition-1_001_cluster-6 -> 5d7ebe4f_hitchkick-competition-1
                // 5d7ebe4f_hitchkick-competition-1_002_set-2 -> 5d7ebe4f_hitchkick-competition-1
                const baseCompetitionCode = competitionCodeForAwards
                  .replace(/_\d{3}_set-\d+$/, '')      // Remove _001_set-1 pattern (old)
                  .replace(/_\d{3}_cluster-\d+$/, ''); // Remove _001_cluster-6 pattern (new)
                
                console.log('View Awards clicked:', { competitionCodeForAwards, baseCompetitionCode });
                const url = `/awards-visualizer/${baseCompetitionCode}`;
                window.open(url, '_blank');
              }}
              tabulatorJudgesCount={connectedJudges.length}
              currentRoutine={currentRoutine ? {
                ...currentRoutine,
                routineNumber: currentRoutine.routineNumber || '',
                name: getRoutineName(currentRoutineData)
              } : null}
              canNavigatePrevious={currentIndex > 0}
              canNavigateNext={currentIndex < routines.length - 1}
              onNavigatePrevious={handlePreviousRoutine}
              onNavigateNext={handleNextRoutine}
              timerProgress={totalSeconds > 0 ? ((totalSeconds - timerSeconds) / totalSeconds) * 100 : 0}
            />
          </div>

          {/* Main content grid - flex-grow with min-h-0 to enable proper scrolling */}
          <div className={`flex-grow min-h-0 grid ${layout.gridCols} ${layout.gap} pb-6 px-6`}>
            {/* Left Column - Schedule/Score Summary */}
            <div 
              className="col-span-1 min-h-0 flex flex-col overflow-hidden"
            >
              {showScoreSummary ? (
                <StageCard className={`flex-grow min-h-0 overflow-hidden ${layout.cardPadding}`} glowColor="purple">
                  <div className="flex-grow min-h-0 overflow-auto">
                    <ScoreSummary
                      finalizedScores={finalizedScores}
                      routines={routinesData}
                      onSelectRoutine={handleRoutineSelect}
                      className="h-full"
                      eventTimezone={eventTimezone}
                    />
                  </div>
                </StageCard>
              ) : (
                <StageCard className={`flex-grow min-h-0 overflow-hidden ${layout.cardPadding}`} glowColor="pink">
                  <PerformanceSchedule
                    routines={routinesData as any[]}
                    currentIndex={currentIndex}
                    onRoutineSelect={handleRoutineSelect}
                    onRoutinesUpload={(newRoutines: any[]) => {
                      handleRoutinesUpload(newRoutines as RoutineData[]);
                    }}
                    className="h-full"
                    maxHeight="clamp(500px, calc(100vh - 140px), 900px)"
                    isTabulator={true}
                  />
                </StageCard>
              )}
            </div>

            {/* Middle Column - Routine Metadata and Live Score */}
            <div 
              className="col-span-1 min-h-0 flex flex-col overflow-hidden space-y-6"
            >
              {/* Routine Metadata Card - At the top */}
              <div className="flex-shrink-0">
                <StageCard className={layout.cardPadding} glowColor="purple">
                  <RoutineMetadataCard
                    currentRoutine={currentRoutineData}
                    onScratchRoutine={handleScratchCurrentRoutine}
                    onUnscratchRoutine={handleUnscratchCurrentRoutine}
                    className="h-full"
                  />
                </StageCard>
              </div>
              
              {/* Live Score Monitor - Second */}
              <div className="flex-shrink-0">
                <StageCard className={layout.cardPadding} glowColor="blue">
                  <LiveScoreMonitor
                    currentRoutine={currentRoutineData as any}
                    currentIndex={currentIndex}
                    compact={true}
                    className="h-full"
                    judgeProfiles={judgeProfiles as any}
                    enableScoreEditing={true}
                    onScoreUpdate={handleScoreUpdate}
                    onCommentUpdate={handleCommentUpdate}
                    onStyleFeedbackUpdate={handleStyleFeedbackUpdate}
                    onBooleanFlagUpdate={handleBooleanFlagUpdate}
                    onScoreDelete={handleScoreDelete}
                    onChoreographerScoreReplacement={handleChoreographerScoreReplacement}
                    styleScoreSheets={styleScoreSheets}
                    scoringMode={scoringMode}
                  />
                </StageCard>
              </div>
            </div>

            {/* Right Column - Awards Tracker */}
            <div 
              className="hidden xl:flex col-span-1 min-h-0 flex flex-col"
              style={{ height: '100%' }}
            >
              <StageCard 
                className={`flex-1 min-h-0 flex flex-col overflow-hidden ${layout.cardPadding}`} 
                glowColor="orange"
              >
                <AwardsTracker enableEditing={true} />
              </StageCard>
            </div>
          </div>


          {/* Judges Connection Status - conditionally rendered only when no judges */}
          {connectedJudges.length === 0 && (
            <div
              className={`flex-shrink-0 mt-2 p-3 rounded-lg border ${
                isDarkMode
                  ? "border-gray-700 bg-gray-900"
                  : "border-gray-200 bg-white"
              }`}
            >
              <div className="flex items-center">
                <AlertCircle size={18} className="mr-2 text-yellow-500" />
                <span className="text-sm">No judges currently connected. Waiting for judges to join...</span>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Privacy lock screen overlay */}
      {isBlurred && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div 
            className="text-center p-8 rounded-2xl bg-black/80 backdrop-blur-xl border border-white/20"
          >
            <button
              onClick={() => toggleBlur()}
              className="cursor-pointer"
              aria-label="Click to unlock screen"
            >
              <div>
                <Lock size={64} className="mx-auto mb-4 text-pink-400 hover:text-pink-300 transition-colors" />
              </div>
            </button>
            <h2 className="text-2xl font-bold text-white mb-2">Screen Locked</h2>
            <p className="text-white/70 mb-4">Click the lock icon above or press Ctrl+L to unlock</p>
            <div className="text-xs text-white/50">
              Privacy mode activated
            </div>
          </div>
        </div>
      )}

      {/* Toast notification for alerts */}
      {toast && (
        <div
          className={`fixed bottom-4 right-4 max-w-md p-4 rounded-lg shadow-lg z-50 ${
            toast.type === "success"
              ? "bg-green-100 text-green-800 dark:bg-green-900/70 dark:text-green-300"
              : toast.type === "warning"
              ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/70 dark:text-yellow-300"
              : toast.type === "info"
              ? "bg-blue-100 text-blue-800 dark:bg-blue-900/70 dark:text-blue-300"
              : "bg-red-100 text-red-800 dark:bg-red-900/70 dark:text-red-300"
          }`}
        >
          <div className="flex items-start">
            {toast.type === "success" ? (
              <CheckCircle size={20} className="mr-2 flex-shrink-0" />
            ) : (
              <AlertCircle size={20} className="mr-2 flex-shrink-0" />
            )}
            <div>
              <p className="font-medium">{toast.message}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabulatorPage;
